/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Button from "components/Button";
import { Form, Field } from "react-final-form";
import TextInput from "fields/TextInput";
import TextArea from "fields/TextAreaInput";
import axios from "axios";
import { BASE_API_URL } from "lib/constants";
import LegacyIcon from "components/icons/legacy-icon";
import RichTextField from "components/Contentful/RichTextField";
import { formatName } from "lib/utils";

const ButtonWrapper = styled.section`
  margin: 1rem auto 3rem;
  width: 100%;

  ${({ theme }) => theme.mq.sm`
    max-width: 13.5rem;
  `}
`;

const Error = styled.div`
  color: ${({ theme }) => theme.themeColors.danger};
  font-size: 0.9rem;
`;

const InnerFormWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  max-width: 45.625rem;
  text-align: center;
  width: 100%;
`;

const MarketingForm = styled.form`
  display: grid;
  row-gap: 1.9375rem;
  width: 100%;
`;

const OuterFormWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 5.3125rem 1.5rem 2.625rem 1.5rem;

  ${({ theme }) => theme.mq.lg`
    padding: 5.625rem 1.5rem;
  `}
`;

const SubmittedCardWrapper = styled.div`
  text-align: center;
`;

const SubmittedParagraph = styled.p`
  max-width: 30.625rem;
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  font-size: 1rem;
  font-weight: 400;
`;

const IconWrapper = styled.div`
  margin: 0 auto 2rem auto;
`;

const StyledRichTextWrapper = styled(RichTextField)`
  margin-bottom: 1.625rem;
  p {
    color: ${({ theme }) => theme.themeColors.darkGreen4};
    max-width: 25rem;
  }

  ${({ theme }) => theme.mq.lg`
    margin-bottom: 2.5rem;
    p {
      max-width: 33.125rem;
    }
  `}
`;

const SubmittedCard = () => {
  return (
    <SubmittedCardWrapper>
      <IconWrapper>
        <LegacyIcon name="circle-check-90" />
      </IconWrapper>
      <h2>Thank you!</h2>
      <SubmittedParagraph>
        Thank you for your interest in GoodFriend! Your message was sent and we
        will get back to you shortly!
      </SubmittedParagraph>
    </SubmittedCardWrapper>
  );
};

export default function ProviderEmailSignupBlock({ fields }) {
  const [error, setError] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const copy = fields?.copy;

  const handleGreetingEmail = (body) => {
    axios
      .post(`${BASE_API_URL}/email/greeting`, body)
      .then(({ data }) => {
        setSubmitted(true);
      })
      .catch((err) => {
        const res = err.response;
        const message =
          "There appears to have been an error. Please retry or contact support.";
        setLoading(false);
        setError(message);
      });
  };

  return (
    <OuterFormWrapper>
      {submitted ? (
        <SubmittedCard />
      ) : (
        <InnerFormWrapper>
          <StyledRichTextWrapper richText={copy} textAlignment="center" />
          <Form
            onSubmit={handleGreetingEmail}
            validate={(values) => {
              const errors = {};
              if (!values.first_name) {
                errors.first_name = "First Name is Required";
              }
              if (!values.last_name) {
                errors.last_name = "Last Name is Required";
              }
              if (!values.business_email) {
                errors.business_email = "Email is Required";
              }
              if (!values.business_name) {
                errors.business_name = "Business Name is Required";
              }
              if (!values.business_phone) {
                errors.business_phone = "Phone is Required";
              }
              if (!values.business_zip_code) {
                errors.business_zip_code = "Zip Code is Required";
              }
              return errors;
            }}
            render={({ handleSubmit }) => (
              <MarketingForm onSubmit={handleSubmit}>
                <Field
                  autoComplete="given-name"
                  id="signup-first-name"
                  label="First name"
                  name="first_name"
                  component={TextInput}
                  showRequiredAsterisk
                  parse={formatName}
                />

                <Field
                  autoComplete="family-name"
                  component={TextInput}
                  id="signup-last-name"
                  label="Last name"
                  name="last_name"
                  parse={formatName}
                  showRequiredAsterisk
                />

                <Field
                  autoComplete="email"
                  component={TextInput}
                  id="signup-business-email"
                  label="Business email"
                  name="business_email"
                  showRequiredAsterisk
                />

                <Field
                  id="signup-business-name"
                  component={TextInput}
                  label="Business name"
                  name="business_name"
                  showRequiredAsterisk
                />

                <Field
                  id="signup-business-phone"
                  inputMode="tel"
                  label="Phone number"
                  name="business_phone"
                  component={TextInput}
                  type="tel"
                  showRequiredAsterisk
                />

                <Field
                  id="signup-business-zip"
                  inputMode="numeric"
                  label="Business ZIP code"
                  name="business_zip_code"
                  component={TextInput}
                  showRequiredAsterisk
                />

                <Field
                  id="signup-business-message"
                  label="Your message"
                  name="message"
                  component={TextArea}
                />
                {error ? <Error>{error}</Error> : null}
                <ButtonWrapper>
                  <Button type="submit">Submit</Button>
                </ButtonWrapper>
              </MarketingForm>
            )}
          />
        </InnerFormWrapper>
      )}
    </OuterFormWrapper>
  );
}

ProviderEmailSignupBlock.propTypes = {
  fields: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    copy: PropTypes.object,
  }),
};

ProviderEmailSignupBlock.defaultProps = {
  fields: {
    title: "Want to learn more? We’re ready to meet you.",
    subtitle:
      "No start-up fees, subscriptions, or binding contracts. Let’s connect and discuss how we can help you.",
    copy: "PropTypes.object",
  },
};
